import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Modal from './Modal';
import GitHubReadme from './GitHubReadme';
import './Gallery.css';

// Lazy loading iframe component
const LazyIframe = ({ src, title, className, scale, width = 1280, height = 1024 }) => {
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { rootMargin: '200px' }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      // Disconnect observer completely to prevent memory leaks
      observer.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only observe once - no need for isInView dependency

  return (
    <div ref={containerRef} className="iframe-preview-container">
      {isInView && !hasError ? (
        <iframe
          className={className}
          src={src}
          title={title}
          loading="lazy"
          sandbox="allow-scripts allow-same-origin"
          onError={() => setHasError(true)}
          style={{
            width: `${width}px`,
            height: `${height}px`,
            transformOrigin: 'center center',
            transform: `translate(-50%, -50%) scale(${scale})`,
            border: 'none',
            pointerEvents: 'none'
          }}
        />
      ) : (
        <div className="iframe-loading-placeholder">
          {hasError ? '⚠️' : '⏳'}
        </div>
      )}
    </div>
  );
};

// Transform unified API project to Gallery format
const toGalleryFormat = (p) => ({
  id: p.id,
  title: p.title || p.name,
  description: p.longDesc,
  category: p.galleryCategory || p.category,
  emoji: p.emoji || p.icon,
  techStack: p.techStack || p.tech,
  link: p.url || '',
  hasInteractivePreview: p.hasInteractivePreview || false,
  featured: p.featured,
  featuredOrder: p.featuredOrder,
  imagePreview: p.imagePreview,
  pdfPreview: p.pdfPreview,
  customPreviewUrl: p.customPreviewUrl,
  customPreviewStyle: p.customPreviewStyle,
});

const Gallery = ({ showFeatured = true }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [shieldProject, setShieldProject] = useState(null); // For glass shield overlay
  const [apiProjects, setApiProjects] = useState(null); // Unified API projects (optional override)

  // Fetch projects from unified API (rodericklrenwick.com)
  useEffect(() => {
    fetch('https://rodericklrenwick.com/api/v2/unified-projects.json')
      .then(res => res.json())
      .then(data => {
        const galleryProjects = data.projects
          .filter(p => p.visibility && p.visibility.gallery)
          .map(toGalleryFormat);
        if (galleryProjects.length > 0) {
          setApiProjects(galleryProjects);
        }
      })
      .catch(err => {
        console.warn('Using local projects (unified API unavailable):', err.message);
      });
  }, []);

  // Comprehensive project list with 26 projects across 7 categories
  // Memoized to prevent recreating on every render (fallback if API fails)
  const defaultProjects = useMemo(() => [
    // COMPREHENSIVE Projects (4 cards)
    {
      id: 'ai-knowledge-distribution',
      title: 'AI Knowledge Distribution',
      description: 'Knowledge manifold visualization from 10 years of context and data analysis',
      category: 'comprehensive',
      emoji: '📊',
      techStack: ['Data Viz', 'Analytics', 'AI'],
      link: 'https://rory.software/lfg-manifold.html',
      hasInteractivePreview: true,
      featured: true,
      featuredOrder: 1  // First showcase instance
    },
    {
      id: 'application-ecosystem',
      title: 'Application Ecosystem',
      description: 'Main professional website with navigation through complete project ecosystem',
      category: 'comprehensive',
      emoji: '🌐',
      techStack: ['Hub', 'Navigation', 'Portfolio'],
      link: 'https://www.rodericklrenwick.com/',
      hasInteractivePreview: true
    },
    {
      id: 'readme',
      title: 'A Deep Learned Neophyte',
      description: 'README: Github profile',
      category: 'comprehensive',
      emoji: '🐱',
      techStack: ['AI', 'Deep Learning', 'Computer Vision'],
      link: 'https://github.com/RLR-GitHub/RLR-GitHub',
      hasInteractivePreview: false,
      imagePreview: 'https://opengraph.githubassets.com/1/RLR-GitHub/RLR-GitHub',
      customPreviewStyle: 'deep-learning-cat'
    },
    {
      id: 'portfolio',
      title: 'Portfolio',
      description: 'Comprehensive documentation of Computer Vision and Machine Learning applications',
      category: 'comprehensive',
      emoji: '🚀',
      techStack: ['CV & ML', 'Portfolio', 'Projects'],
      link: 'https://rlr-github.github.io/',
      hasInteractivePreview: true
    },
    
    // INTERACTIVE Projects (4 cards)
    /* TEMPORARILY HIDDEN
    {
      id: 'ai-manifesto',
      title: 'AI Manifesto',
      description: 'Interactive tribute to classic cyberpunk and AI themes, inspired by the movie "Wargames"',
      category: 'interactive',
      emoji: '🎮',
      techStack: ['Creative', 'Interactive', 'AI'],
      link: 'https://rory.computer/',
      hasInteractivePreview: true
    }, */
    {
      id: 'hacker-terminal-resume',
      title: 'Hacker Terminal Resume',
      description: 'Cyberpunk-themed interactive terminal interface for computer enthusiasts',
      category: 'interactive',
      emoji: '💻',
      techStack: ['Interactive UI', 'CLI', 'Terminal'],
      link: 'https://rory.engineer/__A1__HACKER_TERMINAL_RESUME.html',
      hasInteractivePreview: true
    },
    /* TEMPORARILY HIDDEN
    {
      id: 'energy-visualization',
      title: 'Energy Visualization',
      description: '3D visualization representing fusion of hardware and software - Essence of a Builder',
      category: 'interactive',
      emoji: '🔮',
      techStack: ['Three.js', 'WebGL', '3D'],
      link: 'https://rory.engineer/__A1__abstract_energy_visualization.html',
      hasInteractivePreview: true
    }, */
    {
      id: 'skills-nebula',
      title: 'Skills Nebula',
      description: 'Technical nebula graph visualization across multifaceted domains using D3.js',
      category: 'interactive',
      emoji: '🌌',
      techStack: ['D3.js', 'Visualization', 'Data'],
      link: 'https://rory.engineer/__A1__skills_nebula.html',
      hasInteractivePreview: true
    },
    
    // PROFESSIONAL Projects (2 cards)
    /* TEMPORARILY HIDDEN
    {
      id: 'core-resume',
      title: 'Core Resume (TL;DR)',
      description: 'Comprehensive professional summary and qualifications documentation',
      category: 'professional',
      emoji: '📄',
      techStack: ['Resume', 'CV', 'Professional'],
      link: 'https://rlr-github.github.io/2025resume.html',
      hasInteractivePreview: true
    }, */
    {
      id: 'featured-projects',
      title: 'Featured Projects',
      description: 'Latest curated collection of significant projects and technical implementations',
      category: 'professional',
      emoji: '📌',
      techStack: ['Current', 'Featured', 'Portfolio'],
      link: 'https://rlr-github.github.io/2025portfolio.html',
      hasInteractivePreview: true
    },
    
    // FOUNDATION Projects (2 cards)
    /* TEMPORARILY HIDDEN  
    {
      id: 'who-am-i-rory',
      title: 'Who Am I? Rory.',
      description: 'Original undergraduate webpage showcasing early project developments and engineering journey',
      category: 'foundation',
      emoji: '🎓',
      techStack: ['Origin Story', 'Legacy', 'Education'],
      link: 'https://www.r0ry.com/',
      hasInteractivePreview: true
    }, */
    {
      id: 'neural-network-theory',
      title: 'Neural Network Theory',
      description: 'High-level mathematics for CV and ML implementations from various courses',
      category: 'foundation',
      emoji: '📚',
      techStack: ['Theory', 'Mathematics', 'Academic'],
      link: 'https://www.r0ry.com/html/class_notes.html',
      hasInteractivePreview: true
    },
    
    // HARDWARE Projects (4 cards)
    {
      id: 'mlp-hw-vhdl',
      title: 'MLP HW Simulation via VHDL',
      description: 'Multi-Layer Perceptron for 2D circle classification in hardware',
      category: 'hardware',
      emoji: '🔧',
      techStack: ['VHDL', 'FPGA', 'Hardware'],
      link: 'https://www.r0ry.com/html/vhdl_mlp.html',
      hasInteractivePreview: true,
      pdfPreview: 'https://www.r0ry.com/presentations/ECE475_FinalProject_RLR.pdf#page=8'
    },
    {
      id: 'smart-cat-door',
      title: 'UNet + CNN for Smart Cat Door',
      description: 'UNet segmentation and CNN classification for unique cat detection and door actuation',
      category: 'hardware',
      emoji: '🐱',
      techStack: ['Deep Learning', 'IoT', 'Computer Vision'],
      link: 'https://www.r0ry.com/html/smart_cat_door.html',
      hasInteractivePreview: true
    },
    {
      id: 'smart-parking',
      title: 'CNN for Smart Parking System',
      description: 'CNN-based parking vacancy detection system with full-stack implementation',
      category: 'hardware',
      emoji: '🚗',
      techStack: ['CNN', 'Full-Stack', 'IoT'],
      link: 'https://www.r0ry.com/html/park_smart.html',
      hasInteractivePreview: true
    },
    {
      id: 'vlsi-alu',
      title: 'VLSI 2-bit ALU Design',
      description: '2-bit ALU and digital logic implementations at transistor level',
      category: 'hardware',
      emoji: '📐',
      techStack: ['VLSI', 'CMOS', 'Circuit Design'],
      link: 'https://www.r0ry.com/html/vlsi_design.html',
      hasInteractivePreview: true
    },
    
    // SOFTWARE Projects (4 cards)
    {
      id: 'wgan-gp-cat',
      title: 'WGAN-GP Deepfake Cat',
      description: 'Gradient Penalty Wasserstein-GAN for generating synthetic images of personal cat',
      category: 'software',
      emoji: '🎨',
      techStack: ['Generative AI', 'Python', 'PyTorch'],
      link: 'https://www.r0ry.com/html/remy_gans.html',
      hasInteractivePreview: true
    },
    {
      id: 'mlp-sw-python',
      title: 'MLP SW Implementation via Python',
      description: 'Multi-Layer Perceptron software implementation for pattern recognition',
      category: 'software',
      emoji: '🧠',
      techStack: ['Python', 'Neural Networks', 'ML'],
      link: 'https://github.com/RLR-GitHub/ECE370/blob/main/README.md',
      hasInteractivePreview: false,
      imagePreview: '/images/gallery/ece370_drive_screenshot.png'
    },
    {
      id: 'pca-tsne',
      title: 'PCA + T-SNE Dimensionality Reduction',
      description: 'Face Recognition via C++ and neural networks manifold via MatLab',
      category: 'software',
      emoji: '📉',
      techStack: ['C++', 'MatLab', 'Computer Vision'],
      link: 'https://www.r0ry.com/html/more_computer_vision_projects.html',
      hasInteractivePreview: true,
      imagePreview: 'https://www.r0ry.com/images/FaceRec.png'
    },
    {
      id: 'cv-excursions',
      title: 'Computer Vision Excursions',
      description: 'SLAM, image stitching, segmentation, and PCA implementations',
      category: 'software',
      emoji: '🔬',
      techStack: ['Classical CV', 'Algorithms', 'OpenCV'],
      link: 'https://www.r0ry.com/html/more_computer_vision_projects.html',
      hasInteractivePreview: true,
      imagePreview: 'https://www.r0ry.com/images/cat_seg/remi.png'
    },
    
    // GITHUB Projects (4 cards)
    {
      id: 'github-wgan-gp',
      title: 'WGAN-GP Deepfake Cat',
      description: 'Wasserstein GAN with Gradient Penalty for synthetic cat image generation',
      category: 'github',
      emoji: '🤖',
      techStack: ['Python', 'PyTorch', 'GAN'],
      link: 'https://github.com/RLR-GitHub/RemyWGAN-GP',
      hasInteractivePreview: false,
      imagePreview: 'https://github.com/RLR-GitHub/RemyWGAN-GP/raw/master/animation_single_128_laying_down.gif'
    },
    {
      id: 'github-vhdl-mlp',
      title: 'VHDL MLP and Computer HW Design',
      description: 'Hardware implementations including MLP, computer architecture, and VLSI designs',
      category: 'github',
      emoji: '💻',
      techStack: ['VHDL', 'Hardware', 'Digital Design'],
      link: 'https://github.com/RLR-GitHub/MultilayerPerceptron',
      hasInteractivePreview: false,
      imagePreview: '/images/gallery/perceptron.PNG'
    },
    {
      id: 'github-vlsi',
      title: 'VLSI - Computer Architecture',
      description: '2-bit Arithmetic Logic Unit designed at transistor level using CMOS technology',
      category: 'github',
      emoji: '⚡',
      techStack: ['VLSI', 'CMOS', 'ALU'],
      link: 'https://github.com/RLR-GitHub/ECE375/blob/main/ALU_TwoBit.PNG',
      hasInteractivePreview: false,
      imagePreview: '/images/gallery/ALU_TwoBit.PNG'
    },
    {
      id: 'github-advanced-methods',
      title: 'Advanced Computer Methods',
      description: 'Python MLP, C++ PCA and Face Recognition implementations',
      category: 'github',
      emoji: '👁️',
      techStack: ['Python', 'C++', 'Machine Learning'],
      link: 'https://github.com/RLR-GitHub/ECE270',
      hasInteractivePreview: false,
      imagePreview: '/images/gallery/ece370_screenshot.png'
    }
  ], []); // Empty dependency array since projects data is static

  // Use API projects if available, otherwise fall back to defaults
  const projects = apiProjects || defaultProjects;

  // Categories with counts


  // Memoize featured projects to avoid recalculation
  const featuredProjects = useMemo(() => {
    return projects
      .filter(p => p.featured)
      .sort((a, b) => (a.featuredOrder || 999) - (b.featuredOrder || 999));
  }, [projects]);

  // Use useCallback to prevent recreating functions on every render
  const handleProjectClick = useCallback((project) => {
    // Toggle glass shield for the same project, or switch to new project
    if (shieldProject?.id === project.id) {
      setShieldProject(null);
    } else {
      setShieldProject(project);
    }
  }, [shieldProject]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProject(null);
  }, []);

  return (
    <div className="gallery-container">
      {/* Featured Projects Section */}
      {showFeatured && featuredProjects.length > 0 && (
        <section className="featured-section">
          <h2 className="gallery-title">__Welcome2MyPlayground__</h2>
          
          {/* First Featured Project - AI Knowledge Manifold on its own row */}
          <div className="featured-hero-row">
            {(() => {
              const project = featuredProjects[0];
              return (
                <div 
                  key={project.id}
                  className="featured-card featured-hero clickable"
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="featured-image-container">
                    {/* Live iframe preview for featured projects */}
                    {project.hasInteractivePreview && !project.imagePreview ? (
                      <>
                        <LazyIframe
                          src={project.customPreviewUrl || project.pdfPreview || project.link}
                          title={project.title}
                          className={`featured-iframe-preview ${
                            project.id === 'hacker-terminal-resume' ? 'hacker-terminal-abstract' : 
                            project.id === 'ai-knowledge-distribution' ? 'ai-manifold-abstract' : 
                            project.id === 'featured-projects' ? 'featured-projects-abstract' :
                            'other-featured-abstract'
                          }`}
                          scale={
                            project.id === 'hacker-terminal-resume' ? 0.90 :
                            project.id === 'ai-knowledge-distribution' ? 1.0 :
                            project.id === 'featured-projects' ? 0.95 :
                            0.70
                          }
                        />
                        <span className="live-indicator-badge" data-type={project.pdfPreview ? 'pdf' : 'live'}>
                          <span className="live-dot"></span>
                          {project.pdfPreview ? 'PDF Preview' : 'Live Preview'}
                        </span>
                      </>
                    ) : project.imagePreview ? (
                      <div className="project-image-preview">
                        <img 
                          src={project.imagePreview} 
                          alt={project.title}
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const span = document.createElement('span');
                            span.className = 'preview-emoji';
                            span.textContent = project.emoji;
                            e.target.parentElement.appendChild(span);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="project-preview-placeholder">
                        <span className="preview-emoji">{project.emoji}</span>
                      </div>
                    )}
                    {/* Glass Shield for Featured Cards */}
                    <div className={`glass-shield ${shieldProject?.id === project.id ? 'active' : ''}`}>
                      <div className="shield-content">
                        <div className="url-display">
                          <span className="url-label">PROJECT URL</span>
                          <a href={project.link} target="_blank" rel="noopener noreferrer" 
                             className="url-link neon-pulse">
                            {project.link}
                          </a>
                        </div>
                        <button className="shield-close" onClick={(e) => {
                          e.stopPropagation();
                          setShieldProject(null);
                        }}>×</button>
                      </div>
                    </div>
                    <div className={`featured-overlay ${hoveredProject === project.id ? 'active' : ''}`}>
                      <span className="view-project">Click for URL →</span>
                    </div>
                  </div>
                  <div className="featured-content">
                    <span className={`project-category-badge category-${project.category}`}>
                      {project.category}
                    </span>
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                    <div className="project-tags">
                      {project.techStack.map(tag => (
                        <span key={tag} className="project-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          
          {/* Remaining Featured Projects in a grid */}
          {featuredProjects.length > 1 && (
            <div className="featured-grid">
              {featuredProjects.slice(1).map(project => (
                <div 
                  key={project.id}
                  className="featured-card clickable"
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="featured-image-container">
                    {/* Live iframe preview for featured projects */}
                    {project.hasInteractivePreview && !project.imagePreview ? (
                      <>
                        <LazyIframe
                          src={project.customPreviewUrl || project.pdfPreview || project.link}
                          title={project.title}
                          className={`featured-iframe-preview ${
                            project.id === 'hacker-terminal-resume' ? 'hacker-terminal-abstract' : 
                            project.id === 'ai-knowledge-distribution' ? 'ai-manifold-abstract' : 
                            project.id === 'featured-projects' ? 'featured-projects-abstract' :
                            'other-featured-abstract'
                          }`}
                          scale={
                            project.id === 'hacker-terminal-resume' ? 0.90 :
                            project.id === 'ai-knowledge-distribution' ? 1.0 :
                            project.id === 'featured-projects' ? 0.95 :
                            0.70
                          }
                        />
                        <span className="live-indicator-badge" data-type={project.pdfPreview ? 'pdf' : 'live'}>
                          <span className="live-dot"></span>
                          {project.pdfPreview ? 'PDF Preview' : 'Live Preview'}
                        </span>
                      </>
                    ) : project.imagePreview ? (
                      <div className="project-image-preview">
                        <img 
                          src={project.imagePreview} 
                          alt={project.title}
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const span = document.createElement('span');
                            span.className = 'preview-emoji';
                            span.textContent = project.emoji;
                            e.target.parentElement.appendChild(span);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="project-preview-placeholder">
                        <span className="preview-emoji">{project.emoji}</span>
                      </div>
                    )}
                    {/* Glass Shield for Featured Cards */}
                    <div className={`glass-shield ${shieldProject?.id === project.id ? 'active' : ''}`}>
                      <div className="shield-content">
                        <div className="url-display">
                          <span className="url-label">PROJECT URL</span>
                          <a href={project.link} target="_blank" rel="noopener noreferrer" 
                             className="url-link neon-pulse">
                            {project.link}
                          </a>
                        </div>
                        <button className="shield-close" onClick={(e) => {
                          e.stopPropagation();
                          setShieldProject(null);
                        }}>×</button>
                      </div>
                    </div>
                    <div className={`featured-overlay ${hoveredProject === project.id ? 'active' : ''}`}>
                      <span className="view-project">Click for URL →</span>
                    </div>
                  </div>
                  <div className="featured-content">
                    <span className={`project-category-badge category-${project.category}`}>
                      {project.category}
                    </span>
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                    <div className="project-tags">
                      {project.techStack.map(tag => (
                        <span key={tag} className="project-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* All Projects Gallery */}
      <section className="all-projects-section">

        {/* Projects Grid */}
        <div className="projects-grid">
          {projects.map(project => (
            <div 
              key={project.id}
              className="project-card clickable"
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              onClick={() => handleProjectClick(project)}
            >
              <div className="project-image-container">
                {/* Live iframe preview, GitHub README, image preview, or placeholder */}
                {project.githubRepo ? (
                  <>
                    <GitHubReadme 
                      repoUrl={project.githubRepo}
                      className="project-readme-preview"
                    />
                    <span className="mini-live-badge" data-type="readme">
                      README
                    </span>
                  </>
                ) : project.imagePreview ? (
                  <div className="project-image-preview">
                    <img 
                      src={project.imagePreview} 
                      alt={project.title}
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const span = document.createElement('span');
                        span.className = 'preview-emoji';
                        span.textContent = project.emoji;
                        e.target.parentElement.appendChild(span);
                      }}
                    />
                  </div>
                ) : project.hasInteractivePreview ? (
                  <>
                    <LazyIframe
                      src={project.customPreviewUrl || project.pdfPreview || project.link}
                      title={project.title}
                      className={`project-iframe-preview ${project.id === 'hacker-terminal-resume' ? 'hacker-terminal-project-zoom' : ''}`}
                      scale={project.id === 'hacker-terminal-resume' ? 0.35 : 0.26}
                    />
                    <span className="mini-live-badge" data-type={project.pdfPreview ? 'pdf' : 'live'}>
                      {project.pdfPreview ? 'PDF' : 'Live'}
                    </span>
                  </>
                ) : (
                  <div className="project-preview-placeholder">
                    <span className="preview-emoji">{project.emoji}</span>
                  </div>
                )}
                {/* Glass Shield Overlay */}
                <div className={`glass-shield ${shieldProject?.id === project.id ? 'active' : ''}`}>
                  <div className="shield-content">
                    <div className="url-display">
                      <span className="url-label">URL</span>
                      <a href={project.link} target="_blank" rel="noopener noreferrer" 
                         className="url-link neon-pulse">
                        {project.link}
                      </a>
                    </div>
                    <button className="shield-close" onClick={(e) => {
                      e.stopPropagation();
                      setShieldProject(null);
                    }}>×</button>
                  </div>
                </div>
                <div className={`project-overlay ${hoveredProject === project.id ? 'active' : ''}`}>
                  <span className="view-project-btn">View URL</span>
                </div>
              </div>
              <div className="project-info">
                <span className={`category-badge category-${project.category}`}>
                  {project.category}
                </span>
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-tags">
                  {project.techStack.slice(0, 2).map(tag => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* Modal */}
      <Modal 
        isOpen={isModalOpen}
        onClose={closeModal}
        project={selectedProject}
      />
    </div>
  );
};

export default Gallery;